// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here leaves the device — only the derived-signals slice goes to the brain (brain.ts).
//
// CHILDREN domain — HARD fence: this app holds sizes / dates / inventory ONLY. No
// health, growth, development, or medical field exists in any type below.

import { invoke } from "./tauri-web";

// ── Child ──────────────────────────────────────────────────────────────────────
export interface Child {
  id: string;
  name: string;
  /** Optional birth year — used ONLY for plain age-in-years logistics context (never health). */
  birth_year: number | null;
  created_at: string;
  updated_at: string;
}
export interface NewChild {
  name: string;
  birth_year?: number | null;
}
export type ChildPatch = Partial<{ name: string; birth_year: number | null }>;

// ── Size record ────────────────────────────────────────────────────────────────
export interface SizeRecord {
  id: string;
  child_id: string;
  /** Clothing/shoe category — shoes / tops / bottoms / coats / … */
  category: string;
  /** Human size as she'd read it on a label — "13", "5T", "EU 32". */
  size_label: string;
  /** Optional numeric form of the label, for change-per-month arithmetic. */
  size_value: number | null;
  /** Optional brand (for brand-fit quirks). "" when not given. */
  brand: string;
  /** Date the size was recorded for (local tz), "YYYY-MM-DD". */
  recorded_on: string;
  /** Optional free-text note (e.g. "runs small"). */
  note: string;
  created_at: string;
  updated_at: string;
}
export interface NewSizeRecord {
  child_id: string;
  category: string;
  size_label: string;
  size_value?: number | null;
  brand?: string;
  recorded_on?: string;
  note?: string;
}
export type SizeRecordPatch = Partial<{
  category: string;
  size_label: string;
  size_value: number | null;
  brand: string;
  recorded_on: string;
  note: string;
}>;

// ── Inventory (clothes bought ahead) ────────────────────────────────────────────
export interface InventoryItem {
  id: string;
  child_id: string;
  category: string;
  size_label: string;
  size_value: number | null;
  description: string;
  for_season: string;
  bought_on: string;
  note: string;
  /** On-device photo of the garment (compressed-JPEG data-URL), or "". Never sent to the brain. */
  photo_ref: string;
  created_at: string;
  updated_at: string;
}
export interface NewInventoryItem {
  child_id: string;
  category: string;
  size_label: string;
  size_value?: number | null;
  description?: string;
  for_season?: string;
  bought_on?: string;
  note?: string;
  photo_ref?: string;
}
export type InventoryPatch = Partial<{
  category: string;
  size_label: string;
  size_value: number | null;
  description: string;
  for_season: string;
  bought_on: string;
  note: string;
  photo_ref: string;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates go to the brain) ──
// Mirrors src-tauri/src/signals.rs::ChildSignals. Every field is a computed size,
// date, count, or delta — never a raw note. This is the payload the Sizing Recap
// reasons over (see brain.ts) so it can name SPECIFIC numbers, never re-read the log.
export interface Overall {
  total_records: number;
  total_inventory: number;
  categories_tracked: number;
  first_recorded: string | null;
  last_recorded: string | null;
  days_span: number;
  unit_note: string;
}
export interface CurrentSize {
  category: string;
  size_label: string;
  size_value: number | null;
  brand: string;
  recorded_on: string;
  months_since: number;
}
export interface CategoryVelocity {
  category: string;
  earliest_label: string;
  earliest_value: number;
  earliest_on: string;
  latest_label: string;
  latest_value: number;
  latest_on: string;
  months_elapsed: number;
  sizes_gained: number;
  sizes_per_month: number;
  records_in_category: number;
}
export interface BrandDiscrepancy {
  category: string;
  brand_a: string;
  size_a_label: string;
  size_a_value: number;
  brand_b: string;
  size_b_label: string;
  size_b_value: number;
  size_gap: number;
  days_apart: number;
}
export interface AgeMilestone {
  category: string;
  size_label: string;
  age_years: number;
  recorded_on: string;
}
export interface AheadBought {
  item_id: string;
  category: string;
  size_label: string;
  size_value: number | null;
  description: string;
  for_season: string;
  current_size_label: string | null;
  current_size_value: number | null;
  sizes_ahead: number | null;
  fit: "fits now" | "ahead" | "outgrown" | "unknown";
}
export interface ChildSignals {
  schema: string;
  generated_for: string;
  child_name: string;
  current_age_years: number | null;
  overall: Overall;
  current_sizes: CurrentSize[];
  velocities: CategoryVelocity[];
  brand_discrepancies: BrandDiscrepancy[];
  age_milestones: AgeMilestone[];
  ahead_bought: AheadBought[];
}

export const store = {
  // Children
  listChildren: () => invoke<Child[]>("list_children"),
  getChild: (id: string) => invoke<Child | null>("get_child", { id }),
  createChild: (input: NewChild) => invoke<Child>("create_child", { input }),
  updateChild: (id: string, patch: ChildPatch) => invoke<void>("update_child", { id, patch }),
  deleteChild: (id: string) => invoke<void>("delete_child", { id }),
  // Size records
  listSizeRecords: (childId?: string) => invoke<SizeRecord[]>("list_size_records", { childId: childId ?? null }),
  getSizeRecord: (id: string) => invoke<SizeRecord | null>("get_size_record", { id }),
  createSizeRecord: (input: NewSizeRecord) => invoke<SizeRecord>("create_size_record", { input }),
  updateSizeRecord: (id: string, patch: SizeRecordPatch) => invoke<void>("update_size_record", { id, patch }),
  deleteSizeRecord: (id: string) => invoke<void>("delete_size_record", { id }),
  // Inventory
  listInventory: (childId?: string) => invoke<InventoryItem[]>("list_inventory", { childId: childId ?? null }),
  getInventoryItem: (id: string) => invoke<InventoryItem | null>("get_inventory_item", { id }),
  createInventoryItem: (input: NewInventoryItem) => invoke<InventoryItem>("create_inventory_item", { input }),
  updateInventoryItem: (id: string, patch: InventoryPatch) => invoke<void>("update_inventory_item", { id, patch }),
  deleteInventoryItem: (id: string) => invoke<void>("delete_inventory_item", { id }),
  /** Compute the derived-signals object on-device for ONE child. `today` = client local YYYY-MM-DD. */
  computeSignals: (childId: string, today: string) => invoke<ChildSignals>("compute_signals", { childId, today }),
};

// ── Pure helpers (no I/O) ─────────────────────────────────────────────────────

/** Curated category suggestions; she can also type her own. */
export const DEFAULT_CATEGORIES = [
  "Shoes",
  "Tops",
  "Bottoms",
  "Coats",
  "Dresses",
  "Pyjamas",
  "Swimwear",
  "Accessories",
] as const;

/** Season suggestions for ahead-bought inventory. */
export const SEASONS = ["Spring", "Summer", "Autumn", "Winter", "Next winter", "Next summer"] as const;

/** A small emoji per category, purely decorative. Falls back to a tag glyph. */
export function emojiForCategory(category: string): string {
  const key = category.trim().toLowerCase();
  const map: Record<string, string> = {
    shoes: "👟",
    tops: "👕",
    bottoms: "👖",
    coats: "🧥",
    dresses: "👗",
    pyjamas: "🛏️",
    swimwear: "🩱",
    accessories: "🧦",
  };
  return map[key] ?? "🏷️";
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Friendly "Jun 14, 2026" from a YYYY-MM-DD string (UTC-safe display). */
export function prettyDate(iso: string): string {
  if (!iso) return "";
  const d = toLocalDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** Plain "12 years old" style label from a birth year; null when unknown/implausible. */
export function ageLabel(birthYear: number | null, today = new Date()): string | null {
  if (birthYear == null) return null;
  const age = today.getFullYear() - birthYear;
  if (age < 0 || age > 25) return null;
  return age === 1 ? "1 year old" : `${age} years old`;
}

/** Human label for the ahead-bought fit gap — observational, no advice. */
export function fitLabel(fit: AheadBought["fit"], sizesAhead: number | null): string {
  if (fit === "fits now") return "fits her current size";
  if (fit === "outgrown") return "below her current size";
  if (fit === "ahead") {
    if (sizesAhead == null) return "ahead of her current size";
    const n = Math.round(sizesAhead * 10) / 10;
    return `${n > 0 ? "+" : ""}${n} ahead of current`;
  }
  return "no current size to compare";
}

/** Months-since helper used by the recent list. */
export function monthsSinceLabel(months: number): string {
  if (months < 1) return "this month";
  const m = Math.round(months);
  if (m === 1) return "1 month ago";
  if (m < 12) return `${m} months ago`;
  const y = Math.floor(m / 12);
  const rem = m % 12;
  const yPart = y === 1 ? "1 year" : `${y} years`;
  return rem === 0 ? `${yPart} ago` : `${yPart} ${rem}mo ago`;
}

// ── Fit-check (the app's spine) ─────────────────────────────────────────────────
// The buying-moment verdict: "this brand's size X for this child, right now — buy-ahead,
// fits now, or already outgrown?" It is computed ENTIRELY on-device from the sizes the
// parent has already logged (size_records) — no growth model, no prediction, no health.
// Same ±0.25-of-a-size threshold as the Ready-for-next fit bands so a verdict can never
// disagree with a band.

/** Pull a numeric size out of a label as read on a tag — "116"→116, "EU 32"→32, "5T"→5,
 *  "UK 4.5"→4.5. Returns null when there's no number to compare on. */
export function parseSizeValue(label: string): number | null {
  const m = /(\d+(?:\.\d+)?)/.exec(label ?? "");
  return m ? Number(m[1]) : null;
}

export type FitVerdict = "buy-ahead" | "fits-now" | "outgrown" | "unknown";
export interface FitCheckResult {
  verdict: FitVerdict;
  /** Numeric value parsed from the queried size label, or null when unparseable. */
  value: number | null;
  /** The child's most-recent logged size in that category (the reference the verdict is measured against). */
  currentLabel: string | null;
  currentValue: number | null;
  /** queried value − current value, rounded to 2dp; null when either side is missing. */
  delta: number | null;
  /** The parent's own most-recent logged record for THIS brand+category (the brand-fit history), if any. */
  brandLabel: string | null;
  brandNote: string | null;
  /** Why it couldn't decide (for the "unknown" verdict): "no-number" | "no-current" | null. */
  reason: "no-number" | "no-current" | null;
}

/**
 * Compute the fit verdict for a queried (category, brand, sizeLabel) against ONE child's records.
 * Pure — no I/O, no billed call. Observational only.
 */
export function fitCheck(records: SizeRecord[], category: string, brand: string, sizeLabel: string): FitCheckResult {
  const cat = category.trim().toLowerCase();
  const value = parseSizeValue(sizeLabel);

  // Latest-by-date, not by array position: native SQLite returns newest-first but the web
  // WebStore returns insertion order, so pick the max recorded_on (tie-break created_at) to be
  // correct on BOTH surfaces.
  const latest = (list: SizeRecord[]): SizeRecord | null =>
    list.reduce<SizeRecord | null>((best, r) =>
      !best || r.recorded_on > best.recorded_on || (r.recorded_on === best.recorded_on && r.created_at > best.created_at) ? r : best, null);

  // The child's current size in that category = latest record with a number.
  const cur = latest(records.filter((r) => r.category.trim().toLowerCase() === cat && r.size_value != null));
  const currentValue = cur?.size_value ?? null;
  const currentLabel = cur?.size_label ?? null;

  // The parent's own history for this exact brand+category (case-insensitive), latest first.
  const b = brand.trim().toLowerCase();
  const brandRec = b
    ? latest(records.filter((r) => r.category.trim().toLowerCase() === cat && r.brand.trim().toLowerCase() === b))
    : null;
  const brandLabel = brandRec?.size_label ?? null;
  const brandNote = brandRec?.note?.trim() ? brandRec.note.trim() : null;

  if (value == null) return { verdict: "unknown", value, currentLabel, currentValue, delta: null, brandLabel, brandNote, reason: "no-number" };
  if (currentValue == null) return { verdict: "unknown", value, currentLabel, currentValue, delta: null, brandLabel, brandNote, reason: "no-current" };

  const delta = Math.round((value - currentValue) * 100) / 100;
  const verdict: FitVerdict = delta > 0.25 ? "buy-ahead" : delta < -0.25 ? "outgrown" : "fits-now";
  return { verdict, value, currentLabel, currentValue, delta, brandLabel, brandNote, reason: null };
}

/** Distinct categories present across a child's records (for the picker memory). */
export function categoriesIn(records: SizeRecord[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of records) {
    const c = r.category.trim();
    if (c && !seen.has(c.toLowerCase())) {
      seen.add(c.toLowerCase());
      out.push(c);
    }
  }
  return out;
}
